import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Star, CheckCircle2, MessageSquareHeart } from "lucide-react";
import api from "../utils/api";

export default function PublicFeedbackPage() {
  const { slug } = useParams();
  const [restaurant, setRestaurant] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/api/public/restaurant/${slug}`)
      .then(res => setRestaurant(res.data))
      .catch(() => {});
  }, [slug]);

  const restaurantName = restaurant?.name || "Restaurant";

  const submit = async () => {
    if (!rating) { setError("Please tap a star rating first"); return; }
    setError("");
    setSubmitting(true);
    try {
      await api.post(`/api/public/restaurant/${slug}/feedback`, {
        rating,
        comment,
        customerName: name,
      });
      setDone(true);
    } catch {
      setError("Something went wrong — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    const happy = rating >= 4;
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-rose-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full text-center">
          <CheckCircle2 size={48} className="mx-auto text-emerald-500 mb-4" />
          <h1 className="text-lg font-black text-slate-800 mb-1">
            {happy ? "Thank you! 🎉" : "Thank you for telling us 🙏"}
          </h1>
          <p className="text-sm text-slate-500 leading-relaxed">
            {happy
              ? `We're so glad you enjoyed your visit to ${restaurantName}! Loved it? A Google review would make our day.`
              : `We're sorry we fell short. The team at ${restaurantName} reads every message and will do better on your next visit.`}
          </p>
          {happy && (
            <a
              href={`https://www.google.com/search?q=${encodeURIComponent(restaurantName + " reviews")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-block px-5 py-2.5 bg-slate-800 text-white text-sm font-bold rounded-xl hover:bg-slate-700"
            >
              Review us on Google ★
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-rose-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-6 text-center">
          <div className="w-12 h-12 mx-auto rounded-2xl bg-white/15 border border-white/25 flex items-center justify-center mb-2">
            <MessageSquareHeart size={22} className="text-white" />
          </div>
          <h1 className="text-lg font-black text-white">{restaurantName}</h1>
          <p className="text-[11px] text-amber-100 mt-0.5">How was your experience?</p>
        </div>

        <div className="p-6 space-y-5">
          {/* Stars */}
          <div className="flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map(n => (
              <button
                key={n}
                onClick={() => setRating(n)}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                className="transition-transform active:scale-90"
              >
                <Star
                  size={40}
                  className={(hover || rating) >= n ? "text-amber-400 fill-amber-400" : "text-slate-200"}
                />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <p className="text-center text-sm font-bold text-slate-600">
              {["", "Very poor 😞", "Poor 😕", "Okay 🙂", "Good 😃", "Excellent 🤩"][rating]}
            </p>
          )}

          <textarea
            value={comment}
            onChange={e => setComment(e.target.value)}
            rows={3}
            placeholder={rating > 0 && rating <= 3 ? "What went wrong? We'd really like to fix it…" : "Tell us more (optional)"}
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
          />

          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Your name (optional)"
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
          />

          {error && (
            <p className="text-xs font-semibold text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{error}</p>
          )}

          <button
            onClick={submit}
            disabled={submitting}
            className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold rounded-xl transition-colors disabled:opacity-50"
          >
            {submitting ? "Sending…" : "Send Feedback"}
          </button>
        </div>
      </div>
    </div>
  );
}
